import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { apiUrl } from "../config/api";
import { adminAuthHeaders } from "../utils/authHeaders";
import { formatDisplayNumber, formatNumberInput, parseFormattedNumber } from "../utils/numberFormat";

const TRANSACTION_TYPES = {
  GOLD_PAYMENT: "پرداخت طلا",
  GOLD_SETTLEMENT: "تسویه طلا",
  MONEY_PAYMENT: "پرداخت پولی",
  MONEY_SETTLEMENT: "تسویه پولی",
};

const isGoldTransaction = (title) =>
  title === TRANSACTION_TYPES.GOLD_PAYMENT || title === TRANSACTION_TYPES.GOLD_SETTLEMENT;

const isMoneyTransaction = (title) =>
  title === TRANSACTION_TYPES.MONEY_PAYMENT || title === TRANSACTION_TYPES.MONEY_SETTLEMENT;

const isPaymentTransaction = (title) =>
  title === TRANSACTION_TYPES.GOLD_PAYMENT || title === TRANSACTION_TYPES.MONEY_PAYMENT;

const BalanceEditor = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    goldWeight: "",
    goldAssay: "",
    moneyAmount: "",
  });
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(apiUrl("/api/users"), {
        headers: adminAuthHeaders(),
      });
      const data = await response.json();

      if (data.status === "success") {
        setUsers(data.users);
      } else {
        alert("خطا در دریافت لیست کاربران");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: TRANSACTION_TYPES.GOLD_PAYMENT,
      goldWeight: "",
      goldAssay: "750",
      moneyAmount: "",
    });
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    resetForm();
    setMessage("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    setMessage("");
  };

  const handleTitleChange = (e) => {
    const nextTitle = e.target.value;
    setFormData({
      title: nextTitle,
      goldWeight: "",
      goldAssay: isGoldTransaction(nextTitle) ? "750" : "",
      moneyAmount: "",
    });
  };

  const handleChange = (e) => {
    const numericFields = ["goldWeight", "goldAssay", "moneyAmount"];
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: numericFields.includes(e.target.name)
        ? formatNumberInput(e.target.value, {
            allowDecimal: true,
            allowNegative: false,
          })
        : e.target.value,
    }));
  };

  const getParsedValue = (value) =>
    parseFloat(parseFormattedNumber(value, { allowDecimal: true })) || 0;

  const parsedGoldWeight = getParsedValue(formData.goldWeight);
  const parsedGoldAssay = getParsedValue(formData.goldAssay);
  const parsedMoneyAmount = getParsedValue(formData.moneyAmount);
  const convertedGoldWeight =
    parsedGoldWeight > 0 && parsedGoldAssay > 0
      ? (parsedGoldWeight * parsedGoldAssay) / 750
      : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      if (!formData.title) {
        throw new Error("موضوع تراکنش را انتخاب کنید.");
      }

      const isGold = isGoldTransaction(formData.title);
      const isMoney = isMoneyTransaction(formData.title);
      const isPayment = isPaymentTransaction(formData.title);

      if (isGold && (parsedGoldWeight <= 0 || parsedGoldAssay <= 0)) {
        throw new Error("وزن و عیار را کامل وارد کنید.");
      }

      if (isMoney && parsedMoneyAmount <= 0) {
        throw new Error("مبلغ را کامل وارد کنید.");
      }

      const goldAmount = isGold
        ? (isPayment ? convertedGoldWeight : -convertedGoldWeight)
        : null;
      const moneyAmount = isMoney
        ? (isPayment ? parsedMoneyAmount : -parsedMoneyAmount)
        : null;
      const description = isGold
        ? `${formData.title}به وزن ${formatDisplayNumber(parsedGoldWeight)} گرم به عیار ${formatDisplayNumber(
            parsedGoldAssay,
            0
          )} به عبارتی به وزن ${formatDisplayNumber(convertedGoldWeight)} گرم به عیار 750`
        : `${formData.title} به مبلغ ${formatDisplayNumber(parsedMoneyAmount, 0)} انجام شد`;

      const response = await fetch(apiUrl(`/api/users/${selectedUser.id}/balance`), {
        method: "PATCH",
        headers: adminAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          title: formData.title,
          description,
          moneyAmount,
          goldAmount,
        }),
      });

      const result = await response.json();

      if (response.ok && result.status === "success") {
        setMessage("موجودی با موفقیت بروزرسانی شد.");
        fetchUsers();
        setTimeout(closeModal, 1500);
      } else {
        setMessage(result.error || "خطایی رخ داد.");
      }
    } catch (error) {
      setMessage(error.message || "خطا در ارتباط با سرور.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-manager-container">
      <h2>مدیریت کاربران و موجودی</h2>

      <div className="users-list">
        {users.length === 0 ? (
          <p>در حال بارگذاری...</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>نام</th>
                <th>نام خانوادگی</th>
                <th>موجودی پول</th>
                <th>موجودی طلا</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.firstName}</td>
                  <td>{user.lastName}</td>
                  <td>{formatDisplayNumber(user.moneyBalance)} ریال</td>
                  <td>{formatDisplayNumber(user.goldBalance)} گرم</td>
                  <td>
                    <button className="btn-edit" onClick={() => handleEditClick(user)}>
                      ویرایش موجودی
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isModalOpen &&
        selectedUser &&
        createPortal(
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>
                ویرایش موجودی: {selectedUser.firstName} {selectedUser.lastName}
              </h3>

              {message && (
                <div className={`message ${message.includes("موفقیت") ? "success" : "error"}`}>
                  {message}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-group1">
                  <label>موضوع تراکنش:</label>
                  <select name="title" value={formData.title} onChange={handleTitleChange} required>
                    <option value="">انتخاب کنید</option>
                    <option value={TRANSACTION_TYPES.GOLD_PAYMENT}>{TRANSACTION_TYPES.GOLD_PAYMENT}</option>
                    <option value={TRANSACTION_TYPES.GOLD_SETTLEMENT}>{TRANSACTION_TYPES.GOLD_SETTLEMENT}</option>
                    <option value={TRANSACTION_TYPES.MONEY_PAYMENT}>{TRANSACTION_TYPES.MONEY_PAYMENT}</option>
                    <option value={TRANSACTION_TYPES.MONEY_SETTLEMENT}>{TRANSACTION_TYPES.MONEY_SETTLEMENT}</option>
                  </select>
                </div>

                {isGoldTransaction(formData.title) && (
                  <>
                    <div className="form-group1">
                      <label>وزن ورودی:</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        name="goldWeight"
                        placeholder="مثال: 10"
                        value={formData.goldWeight}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group1">
                      <label>عیار:</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        name="goldAssay"
                        value={formData.goldAssay}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group1">
                      <label>وزن 750:</label>
                      <input
                        type="text"
                        value={convertedGoldWeight > 0 ? formatDisplayNumber(convertedGoldWeight) : ""}
                        readOnly
                      />
                    </div>
                  </>
                )}

                {isMoneyTransaction(formData.title) && (
                  <div className="form-group1">
                    <label>مبلغ (ریال):</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      name="moneyAmount"
                      placeholder="مثال: 100000"
                      value={formData.moneyAmount}
                      onChange={handleChange}
                      required
                    />
                  </div>
                )}

                <div className="modal-actions">
                  <button type="submit" disabled={loading} className="btn-submit">
                    {loading ? "در حال پردازش..." : "ثبت تغییرات"}
                  </button>
                  <button type="button" onClick={closeModal} className="btn-cancel">
                    انصراف
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default BalanceEditor;
